import { Clock, MessageSquare, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SaraAvatar } from './sara-avatar';

import { Button } from '~/components/ui/button';
import type { SessionInfo } from '~/lib/interview-api';

interface LandingPageProps {
  session: SessionInfo;
  loading: boolean;
  onStart: (modality: 'text' | 'voice') => void;
}

export function LandingPage({ session, loading, onStart }: LandingPageProps): React.ReactNode {
  const { t } = useTranslation();

  const modalities = session.job.modality;
  const hasText = modalities.includes('text');
  const hasVoice = modalities.includes('voice');
  const hasBoth = hasText && hasVoice;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background px-10 py-8">
      <div className="flex max-w-xl flex-col items-center gap-6 text-center">
        {/* Sara avatar */}
        <SaraAvatar size="lg" />

        {/* Badge */}
        <span className="rounded-full border border-border px-4 py-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {t('screening.landing.badge')}
        </span>

        {/* Job title */}
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{session.job.title}</h1>

        {/* Greeting */}
        <p className="max-w-md text-base leading-relaxed text-muted-foreground">
          {t('screening.landing.greeting', { name: session.candidate.name })}
        </p>

        {/* Stats */}
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Clock className="size-4" />
            {t('screening.landing.minutes', { count: session.job.estimatedMinutes })}
          </span>
          <span className="text-border">|</span>
          <span>{t('screening.landing.questions', { count: session.job.questionCount })}</span>
        </div>

        {/* Action buttons */}
        <div className="flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-4">
          {hasText && (
            <Button
              className="h-12 gap-2 px-6"
              disabled={loading}
              onClick={() => {
                onStart('text');
              }}
            >
              <MessageSquare className="size-4" />
              {t('screening.landing.startChat')}
            </Button>
          )}

          {hasBoth && (
            <span className="text-center text-sm text-muted-foreground">
              {t('screening.landing.or')}
            </span>
          )}

          {hasVoice && (
            <Button
              className="h-12 gap-2 px-6"
              disabled={loading}
              onClick={() => {
                onStart('voice');
              }}
            >
              <Phone className="size-4" />
              {t('screening.landing.startVoice')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
